import { useEffect, useState } from 'react';
import inNetworkOnIcon from '@assets/dashboard/on.svg';
import inNetworkOffIcon from '@assets/dashboard/off.svg';
import endorseIcon from '@assets/dashboard/endorse.svg';
import reviewIcon from '@assets/dashboard/review.svg';
import earnIcon from '@assets/dashboard/earn.svg';
import type { ReactNode } from 'react';
import { Box, Group, Paper, Stack, Text, UnstyledButton } from '@mantine/core';
import useNotification from '@hooks/useNotification';
import EndorsementModal from '../EndorsementModal/EndorsementModal';
import type { EndorsementPayload } from '../EndorsementModal/types';
import {
  createWorkerEndorsementApi,
  fetchEndorsementFormData,
  type EndorsementFormData,
} from './workerEndorsementApi';

export interface ButtonsWidgetProps {
  reviewsUrl: string;
  surveysUrl: string;
  isInNetwork: boolean;
  onInNetworkClick: () => void;
}

const BUTTON_ID_TYPE = {
  IN_NETWORK: 'in-network',
  ENDORSE: 'endorse',
  REVIEW: 'review',
  EARN: 'earn',
} as const;

type ButtonId = (typeof BUTTON_ID_TYPE)[keyof typeof BUTTON_ID_TYPE];

// Gradient colors live in theme/ComponentDefaultCss/colors.scss
const BORDER_GRADIENTS = {
  IN_NETWORK_ON: ['var(--in-network-on-from)', 'var(--in-network-on-to)'],
  IN_NETWORK_OFF: ['var(--in-network-off-from)', 'var(--in-network-off-to)'],
  ENDORSE: ['var(--endorse-from)', 'var(--endorse-to)'],
  REVIEW: ['var(--review-from)', 'var(--review-to)'],
  EARN: ['var(--earn-from)', 'var(--earn-to)'],
};

// In-Network card copy differs by status: "on" celebrates the active perks,
// "off" prompts not-in-network workers to upgrade.
const IN_NETWORK_COPY = {
  on: {
    label: "You're In-Network, getting seen first!",
    description:
      "You're at the top of applied jobs & worker search by location lists, custom profile URL and more...",
  },
  off: {
    label: 'In-Network: Upgrade to stand out more!',
    description:
      'Be at the top of applied lists, worker search by location lists, get new job alerts faster, and more...',
  },
};

interface ButtonConfig {
  id: ButtonId;
  icon: string;
  iconLabel: string;
  label: string;
  description: ReactNode;
  border: string[];
}

const STATIC_BUTTONS: ButtonConfig[] = [
  {
    id: BUTTON_ID_TYPE.ENDORSE,
    icon: endorseIcon,
    iconLabel: 'Endorse a Worker',
    label: 'Endorse a Worker',
    description: (
      <>
        Everyone deserves recognition, and likes receiving it.{' '}
        <strong>Help a fellow worker out!</strong>
      </>
    ),
    border: BORDER_GRADIENTS.ENDORSE,
  },
  {
    id: BUTTON_ID_TYPE.REVIEW,
    icon: reviewIcon,
    iconLabel: 'Review Any Event Job',
    label: 'Review Any Event Job',
    description: (
      <>
        Share your experience, get profile credit by reviewing, plus,{' '}
        <strong>other workers want to know!</strong>
      </>
    ),
    border: BORDER_GRADIENTS.REVIEW,
  },
  {
    id: BUTTON_ID_TYPE.EARN,
    icon: earnIcon,
    iconLabel: 'Earn $',
    label: 'Earn $',
    description: 'taking surveys.',
    border: BORDER_GRADIENTS.EARN,
  },
];

const styles = {
  icon: { width: 50, height: 'auto', display: 'block' },
};

const cardRootStyle = ([from, to]: string[]) => ({
  border: '1px solid transparent',
  background:
    `linear-gradient(var(--white), var(--white)) padding-box, ` +
    `linear-gradient(180deg, ${from}, ${to}) border-box`,
});

export function ButtonsWidget({
  reviewsUrl,
  surveysUrl,
  isInNetwork,
  onInNetworkClick,
}: ButtonsWidgetProps) {
  const [endorseModalOpened, setEndorseModalOpened] = useState(false);
  const [formData, setFormData] = useState<EndorsementFormData | null>(null);
  const [api] = useState(() => createWorkerEndorsementApi());
  const { showSuccessNotification, showErrorNotification } = useNotification();

  useEffect(() => {
    if (endorseModalOpened && !formData) {
      fetchEndorsementFormData()
        .then(setFormData)
        .catch(() =>
          showErrorNotification('Error loading endorsement form data')
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endorseModalOpened]);

  const handleEndorsementSubmit = async (payload: EndorsementPayload) => {
    try {
      await api.saveEndorsement(payload);
      setEndorseModalOpened(false);
      showSuccessNotification('Endorsement submitted successfully!');
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Error submitting endorsement';
      showErrorNotification(message);
    }
  };

  const inNetworkCopy = isInNetwork ? IN_NETWORK_COPY.on : IN_NETWORK_COPY.off;
  const inNetworkButton: ButtonConfig = {
    id: BUTTON_ID_TYPE.IN_NETWORK,
    icon: isInNetwork ? inNetworkOnIcon : inNetworkOffIcon,
    iconLabel: isInNetwork ? 'In-Network on' : 'In-Network off',
    label: inNetworkCopy.label,
    description: inNetworkCopy.description,
    border: isInNetwork
      ? BORDER_GRADIENTS.IN_NETWORK_ON
      : BORDER_GRADIENTS.IN_NETWORK_OFF,
  };

  const buttons = [inNetworkButton, ...STATIC_BUTTONS];

  /**
   * When dashboard is fully migrated to React,
   * replace window.location.href with React Router navigation
   */
  const handleButtonClick = (buttonId: ButtonId) => {
    switch (buttonId) {
      case BUTTON_ID_TYPE.IN_NETWORK:
        onInNetworkClick();
        break;
      case BUTTON_ID_TYPE.ENDORSE:
        setEndorseModalOpened(true);
        break;
      case BUTTON_ID_TYPE.REVIEW:
        window.location.href = reviewsUrl;
        break;
      case BUTTON_ID_TYPE.EARN:
        window.location.href = surveysUrl;
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Stack gap="sm">
        {buttons.map((button) => (
          <Paper
            key={button.id}
            shadow="var(--widget-shadow)"
            p="md"
            styles={{ root: cardRootStyle(button.border) }}
          >
            <UnstyledButton
              onClick={() => handleButtonClick(button.id)}
              w="100%"
              h="100%"
            >
              <Group gap="xl" wrap="nowrap" align="center" px="md">
                <Box>
                  <img
                    src={button.icon}
                    alt={button.iconLabel}
                    style={styles.icon}
                  />
                </Box>
                <Stack gap={2} flex={1} ta="left">
                  <Text fw={700} fz={18} c="#4A4A4A">
                    {button.label}
                  </Text>
                  <Text fz={12} c="#4A4A4A">
                    {button.description}
                  </Text>
                </Stack>
              </Group>
            </UnstyledButton>
          </Paper>
        ))}
      </Stack>

      <EndorsementModal
        opened={endorseModalOpened && formData !== null}
        onClose={() => setEndorseModalOpened(false)}
        onSubmit={handleEndorsementSubmit}
        api={api}
        eventTypes={formData?.eventTypes ?? []}
        traits={formData?.traits ?? []}
      />
    </>
  );
}
